/**
 * Permission Configuration
 * Defines which permissions each role has
 */

import { UserRole, Permission, SecurityLevel } from '../data/Auth.types';

// Public permissions (Level 0 - No authentication)
export const PUBLIC_PERMISSIONS: Permission[] = [
    Permission.VIEW_MARKET,
    Permission.VIEW_MARKET_SUMMARY,
    Permission.VIEW_MARKET_SUMMARY_PERFORMANCE,
    Permission.VIEW_MARKET_SUMMARY_PERFORMANCE_1,    
    Permission.VIEW_MARKET_SUMMARY_BREADTH,
    Permission.VIEW_MARKET_SUMMARY_BREADTH_1,
    Permission.VIEW_MARKET_SUMMARY_INVESTORS,
    Permission.VIEW_MARKET_SUMMARY_INVESTORS_1,
    //Permission.VIEW_MARKET_SUMMARY_SECTORS,
    //Permission.VIEW_MARKET_SUMMARY_SECTORS_1,
    Permission.VIEW_MARKET_NEWS,
   // Permission.VIEW_MARKET_SECTORS,
    Permission.VIEW_MARKET_CALENDAR
];

// Client permissions (Level 1 - Authenticated client)
export const CLIENT_PERMISSIONS: Permission[] = [
    ...PUBLIC_PERMISSIONS,
    Permission.VIEW_MARKET_SUMMARY_SECTORS,
    Permission.VIEW_MARKET_SUMMARY_SECTORS_1,
    Permission.VIEW_MARKET_SECTORS,
    Permission.VIEW_MARKET_RESEARCH,
    Permission.VIEW_MARKET_WATCHLIST,
    Permission.VIEW_MARKET_QUOTE_DETAILS,
    Permission.VIEW_ACCOUNT_OVERVIEW,
    Permission.VIEW_ACCOUNT_POSITIONS,
    Permission.VIEW_ACCOUNT_ACTIVITY,
    Permission.VIEW_ACCOUNT_WALLET,
    Permission.VIEW_ACCOUNT_ORDERS_HISTORY,
    Permission.VIEW_TRADE_TICKET,
    Permission.VIEW_TRADE_LIVE_ORDERS,
    Permission.VIEW_TRADE_HISTORY,
    Permission.EXECUTE_TRADE
];

// ============================================================================
// ROLE PERMISSIONS MAPPING
// ============================================================================
export const ROLE_PERMISSIONS: Record<UserRole, Permission[]> = {
    // Public users - no permissions (rely on PUBLIC_PERMISSIONS)
    [UserRole.PUBLIC_USER]: [],

    // Demo client - all demo authenticated permissions
    [UserRole.DEMO_CLIENT]: [...CLIENT_PERMISSIONS],

    // Regular client - all authenticated permissions
    [UserRole.CLIENT]: [...CLIENT_PERMISSIONS],

    // Regular client - all authenticated permissions
    [UserRole.SUPER_CLIENT]: [...CLIENT_PERMISSIONS],

    [UserRole.HR_USER]: [
        ...CLIENT_PERMISSIONS,
        Permission.VIEW_HR_SECTION,
        Permission.VIEW_DATA,
        Permission.EXPORT_DATA
    ],

    [UserRole.COMPLIANCE_USER]: [
        ...CLIENT_PERMISSIONS,
        Permission.VIEW_COMPLIANCE_TOOLS,
        Permission.VIEW_AUDIT_LOGS,
        Permission.EXPORT_DATA
    ],

    [UserRole.ACCOUNTANT_USER]: [
        ...CLIENT_PERMISSIONS,
        Permission.VIEW_ACCOUNTANT_REPORTS,
        Permission.VIEW_DATA,
        Permission.EXPORT_DATA
    ],

    [UserRole.RESEARCH_USER]: [
        ...CLIENT_PERMISSIONS,
        Permission.VIEW_RESEARCH_SECTION,
        Permission.VIEW_DATA,
        Permission.EDIT_DATA
    ],

    [UserRole.SIGMA_PLUS_USER]: [
        ...CLIENT_PERMISSIONS,
        Permission.VIEW_SIGMA_PLUS_SECTION,
        Permission.VIEW_DATA
    ],

    [UserRole.SUPER_ADMIN]: [
        // Super Admin has all permissions
        ...Object.values(Permission)
    ]
};

// ============================================================================
// PERMISSION HELPERS
// ============================================================================

/**
 * Check if a permission requires authentication
 */
export const requiresAuth = (permission: Permission): boolean => {
    return !PUBLIC_PERMISSIONS.includes(permission);
};

/**
 * Get minimum security level required for a permission
 */
export const getRequiredLevel = (permission: Permission): SecurityLevel => {
    if (PUBLIC_PERMISSIONS.includes(permission)) {
        return SecurityLevel.PUBLIC;
    }
    if (CLIENT_PERMISSIONS.includes(permission)) {
        return SecurityLevel.CLIENT;
    }
    return SecurityLevel.INTERNAL;
};

/**
 * Get all permissions for a specific security level
 */
export const getPermissionsByLevel = (level: SecurityLevel): Permission[] => {
    switch (level) {
        case SecurityLevel.PUBLIC:
            return PUBLIC_PERMISSIONS;
        case SecurityLevel.CLIENT:
            return CLIENT_PERMISSIONS;
        case SecurityLevel.INTERNAL:
            // Return all possible internal permissions
            return Array.from(new Set([
                //...SUPER_ADMIN
            ]));
        default:
            return PUBLIC_PERMISSIONS;
    }
};
/**
 * Check if user has a specific permission
 */
export function hasPermission(
    user: { securityLevel: SecurityLevel; roles?: UserRole[]; permissions?: Permission[] },
    permission: Permission
): boolean {
    // Public permissions are always available
    if (PUBLIC_PERMISSIONS.includes(permission)) {
        return true;
    }

    // Not authenticated - no permissions beyond public
    if (user.securityLevel === SecurityLevel.PUBLIC) {
        return false;
    }

    // Check explicit permissions
    if (user.permissions?.includes(permission)) {
        return true;
    }

    // Check role-based permissions
    if (user.roles) {
        for (const role of user.roles) {
            if (ROLE_PERMISSIONS[role]?.includes(permission)) {
                return true;
            }
        }
    }

    return false;
}

/**
 * Check if user has ALL specified permissions
 */
export function hasAllPermissions(
    user: { securityLevel: SecurityLevel; roles?: UserRole[]; permissions?: Permission[] },
    permissions: Permission[]
): boolean {
    return permissions.every(permission => hasPermission(user, permission));
}

/**
 * Check if user has ANY of the specified permissions
 */
export function hasAnyPermission(
    user: { securityLevel: SecurityLevel; roles?: UserRole[]; permissions?: Permission[] },
    permissions: Permission[]
): boolean {
    return permissions.some(permission => hasPermission(user, permission));
}

/**
 * Get all permissions for a user
 */
export function getUserPermissions(
    user: { securityLevel: SecurityLevel; roles?: UserRole[]; permissions?: Permission[] }
): Permission[] {
    const permissions = new Set<Permission>();

    // Add public permissions (always available)
    PUBLIC_PERMISSIONS.forEach(p => permissions.add(p));

    // If not authenticated, return only public
    if (user.securityLevel === SecurityLevel.PUBLIC) {
        return Array.from(permissions);
    }

    // Add authenticated permissions
    CLIENT_PERMISSIONS.forEach(p => permissions.add(p));

    // Add explicit permissions
    if (user.permissions) {
        user.permissions.forEach(p => permissions.add(p));
    }

    // Add role-based permissions
    if (user.roles) {
        user.roles.forEach(role => {
            ROLE_PERMISSIONS[role]?.forEach(p => permissions.add(p));
        });
    }

    return Array.from(permissions);
}

/**
 * Check if permission is public (no auth required)
 */
export function isPublicPermission(permission: Permission): boolean { return PUBLIC_PERMISSIONS.includes(permission);} (
    userSecurityLevel: SecurityLevel,
    userRoles: UserRole[],
    requiredLevel: SecurityLevel,
    requiredRoles?: UserRole[]
): boolean => {
    // Check security level first
    if (userSecurityLevel < requiredLevel) {
        return false;
    }

    // If no specific roles required, level check is enough
    if (!requiredRoles || requiredRoles.length === 0) {
        return true;
    }

    // Super Admin can access everything
    if (userRoles.includes(UserRole.SUPER_ADMIN)) {
        return true;
    }

    // Check if user has any of the required roles
    return requiredRoles.some(role => userRoles.includes(role));
}